import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import useRateLimitedMutation, { RATE_LIMIT_WINDOW_MS } from '@/app/mutations/Shared/useRateLimitedMutation';
import { TranscriptionContext, Transcription } from '@/app/mutations/TranscriptionMutation/context';
import { StopOptions } from '@/app/mutations/Shared/types';
import fetchTranscription, { FetchTranscriptionParams } from '@/app/mutations/TranscriptionMutation/fetchTranscription';
import { isRateLimitError } from '@/app/utils/errors';
import { useToast } from '@/components/Toast/context';
import { useSentry } from '@/sentry/context';

export default function withTranscriptionMutation<T extends object>(
  Component: (props: T) => ReactNode,
) {
  return function ConnectedComponent(props: T) {
    const { hash } = useParams<{ hash: string }>();

    const Sentry = useSentry();
    const toast = useToast();

    const [isTranscribing, setIsTranscribing] = useState(false);

    const { error, mutate, status, variables } = useRateLimitedMutation({
      async mutationFn(params: { action: FetchTranscriptionParams['action'] } & StopOptions) {
        // `force` (if present) only gates the rate limiter — it is not sent to the server.
        await fetchTranscription({ action: params.action, meeting_id: hash! });
      },
      // Per-mutation, so the outcome of each call is applied even if a later
      // mutate() has already replaced the observer's status. See
      // withRecordingMutation.
      onSuccess(_data, params) {
        setIsTranscribing(params.action === 'start');
        if (params.action === 'start') {
          toast.publish({ text: 'Transcription started', variant: 'success' });
        }
      },
      retry: false,
    }, RATE_LIMIT_WINDOW_MS);

    // See withRecordingMutation: the gate needs the in-flight start separately
    // from `isTranscribing`, which only flips once the server call returns.
    const isStartPending = status === 'pending' && variables?.action === 'start';

    const transcription = useMemo<Transcription>(() => ({
      isTranscribing,
      isStartPending,
      start: () => mutate({ action: 'start' }),
      stop: (options?: StopOptions) => mutate({ action: 'stop', force: options?.force }),
    }), [isTranscribing, isStartPending, mutate]);

    useEffect(function onMutationStatusChange() {
      switch (status) {
        case 'idle':
        case 'pending':
          break;
        case 'success':
          // isTranscribing and the start toast are handled in onSuccess, per mutation.
          break;
        case 'error':
          if (isRateLimitError(error)) {
            console.warn(error.message);
          } else {
            toast.publish({ text: `Transcription ${variables?.action} failed: Please refresh page.`, variant: 'destructive' });
            if (Sentry) {
              Sentry.captureException(error, {
                tags: {
                  operation: 'transcription',
                  action: variables?.action,
                },
                extra: {
                  meetingId: hash,
                  timestamp: new Date().toISOString(),
                },
              });
            }
          }
          break;
        default: {
          const neverType: never = status;
          throw new Error(`Unhandled status "${neverType}" in withTranscriptionMutation`);
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error, status, toast, variables?.action]);

    return (
      <TranscriptionContext.Provider value={transcription}>
        <Component {...props} />
      </TranscriptionContext.Provider>
    );
  };
}
